import React, {
  FunctionComponent,
  useContext,
  useState,
  useEffect,
} from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import * as H from "history";
import {
  useTranslate,
  sendTealiumEvent,
  CONSTANTS,
  IntlContext,
} from "../../helpers";
import routes from "../../app/routes";
import { Pharmacy, PharmacyType, ActionType } from "../../@types";
import { AppStore } from "../../app/store";
import StickyTray from "../../components/StickyTray";

interface FooterProps extends RouteComponentProps {
  selectionList: Pharmacy[];
  onRemovePharmacyFromSelection: (npi: string | number) => void;
  hasMailOrder: boolean;
  onRemoveMailOrder: () => void;
  pageLoading?: boolean;
  warningMessage?: string;
  nextRoute?: H.History.PoorMansUnknown;
}

const PharmacySelectionFooter: FunctionComponent<FooterProps> = props => {
  const t = useTranslate();
  const fm = useContext(IntlContext).formatMessage;
  const { state, dispatch } = useContext(AppStore.AppContext);
  const [footerErrorMessage, setfooterErrorMessage] = useState("");

  const handleDone = (): void => {
    if (!props.selectionList.length && !props.hasMailOrder) {
      setfooterErrorMessage(
        t("pharmacy_selection_page.no_pharmacies_selected")
      );
      return;
    }

    // istanbul ignore next: tested elsewhere
    sendTealiumEvent("Find Plans - Add Pharmacy", "CTA: Done - Next Step");
    sendTealiumEvent(
      "Find Plans - Add Pharmacy",
      `Number of pharmacies submitted ${props.selectionList.length}`
    );
    const { search } = props.location;

    // Adjust the pharmacyType based on what is selected
    const retailSelected = !!props.selectionList.length;
    const mailSelected = props.hasMailOrder;
    let newType;
    if (
      retailSelected &&
      mailSelected &&
      state.pharmacyType !== PharmacyType.MAIL_AND_RETAIL
    ) {
      newType = PharmacyType.MAIL_AND_RETAIL;
    } else if (
      retailSelected &&
      !mailSelected &&
      state.pharmacyType !== PharmacyType.RETAIL
    ) {
      newType = PharmacyType.RETAIL;
    } else if (
      !retailSelected &&
      mailSelected &&
      state.pharmacyType !== PharmacyType.MAIL
    ) {
      newType = PharmacyType.MAIL;
    }

    if (newType) {
      dispatch({
        type: ActionType.UPDATE_PHARMACY_TYPE,
        payload: newType,
      });
    }

    const defaultNextRoute = `${routes.searchResults}${search}`;
    props.history.push(props.nextRoute || defaultNextRoute);
  };

  const items = props.selectionList.map(p => ({
    name: p.name.toLowerCase(),
    id: p.npi,
  }));
  if (props.hasMailOrder) {
    items.push({ name: t("plan_search_prefs.mail_order_pharmacy"), id: "" });
  }

  let screenReaderText = "";
  const totalSelected =
    props.selectionList.length + (props.hasMailOrder ? 1 : 0);
  if (totalSelected < CONSTANTS.pharmacySelection.max - 1) {
    screenReaderText = fm(
      {
        id: "pharmacy_selection_page.footer.select_more_pharmacies",
      },
      { number: CONSTANTS.pharmacySelection.max - totalSelected }
    );
  } else if (totalSelected === CONSTANTS.pharmacySelection.max - 1) {
    screenReaderText = t("pharmacy_selection_page.footer.one_more");
  }

  useEffect(() => {
    setfooterErrorMessage("");
  }, [footerErrorMessage]);

  return (
    <StickyTray
      id="pharmacy-selection-footer"
      className="PharmacySelectionFooter"
      heading={`${items.length} ${
        items.length === 1
          ? t("pharmacy_selection_page.footer.heading.singular")
          : t("pharmacy_selection_page.footer.heading.plural")
      }`}
      items={items}
      maxItems={CONSTANTS.pharmacySelection.max}
      onRemoveItem={(id: string | number): void => {
        if (id) {
          props.onRemovePharmacyFromSelection(id);
        } else {
          props.onRemoveMailOrder();
        }
      }}
      pageLoading={props.pageLoading}
      onSubmit={handleDone}
      linkForSubmit={props.nextRoute as string}
      submitText={t("done")}
      warningMessage={props.warningMessage}
      errorMessage={footerErrorMessage}
      screenReaderText={screenReaderText}
      mobileOpenButtonText={
        items.length === 1
          ? t("pharmacy_selection_page.footer.mobile_open.singular")
          : fm(
              {
                id: "pharmacy_selection_page.footer.mobile_open.plural",
              },
              { number: items.length }
            )
      }
      onMobileToggleVisibility={(visibility: boolean): void => {
        if (visibility) {
          sendTealiumEvent(
            "Find Plans - Add Pharmacy",
            "CTA: View Your Pharmacies"
          );
        } else {
          sendTealiumEvent("Find Plans - Add Pharmacy", "Close Overlay");
        }
      }}
      notStickyOnMobile
    />
  );
};

export default withRouter(PharmacySelectionFooter);
